import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getPublicProfileByUserName } from "../../endpoints/candidate.service";
import { useSnackbar } from "notistack";

const sectionConfig = [
  { key: "about", label: "About" },
  { key: "skills", label: "Skills" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "certifications", label: "Certifications" },
  { key: "contact", label: "Contact" },
];

const PublicProfilePage = () => {
  const { userName } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const authUser = useSelector((state) => state.auth.user);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const isOwner = useMemo(
    () => !!authUser?.userName && authUser.userName === userName,
    [authUser?.userName, userName],
  );

  useEffect(() => {
    if (isOwner) {
      navigate("/my-profile", { replace: true });
      return;
    }
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const { success, data } = await getPublicProfileByUserName(userName);
        if (!success) {
          throw new Error(data?.message || "Unable to load profile.");
        }
        setProfile(data);
      } catch (error) {
        enqueueSnackbar(error, { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    if (userName) fetchProfile();
  }, [isOwner, navigate, enqueueSnackbar, userName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <span className="text-sm text-gray-500">Loading profile...</span>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <span className="text-sm text-gray-500">Profile not found.</span>
      </div>
    );
  }

  const privacy = profile?.privacySettings || {};
  const isPublic = (key) => {
    if (key === "certifications") {
      return privacy?.education === "Public";
    }
    return privacy?.[key] === "Public";
  };
  const sanitizeText = (text) => {
    if (!text) return "";
    return text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  return (
    <div className="h-full min-h-screen bg-gradient-to-b from-[#F8FAFC] via-[#F5F7FB] to-[#F8FAFC] px-4 sm:px-6 py-6 sm:py-10 overflow-y-auto no-scrollbar">
      <div className="mx-auto max-w-5xl pb-10">
        <div className="rounded-2xl bg-white shadow-[0_12px_28px_rgba(15,23,42,0.08)] overflow-hidden">
          <div className="relative">
            <div className="h-24 bg-gradient-to-r from-[#0F172A] via-[#1F2937] to-[#111827]" />
            <div className="px-8 pb-8 pt-0">
              <div className="-mt-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div className="flex items-center gap-5">
                  <div className="h-20 w-20 rounded-2xl bg-white shadow-md p-1">
                    <div className="h-full w-full rounded-xl bg-gray-200 flex items-center justify-center overflow-hidden">
                      {profile?.profilePic ? (
                        <img
                          src={profile.profilePic}
                          alt={`${profile.firstName} ${profile.lastName}`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl font-semibold text-white">
                          {profile?.firstName?.charAt(0)}
                          {profile?.lastName?.charAt(0)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]">
                      {profile?.firstName} {profile?.lastName}
                    </h1>
                    <p className="text-sm text-gray-600">
                      {profile?.jobTitle || "Candidate"}
                    </p>
                    <p className="text-xs text-gray-500">{profile?.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-[#F1F5F9] px-3 py-1 text-[11px] font-semibold tracking-wide text-[#475569]">
                    Public Profile
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 sm:px-8 pb-10">
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
              {sectionConfig.map((section) => {
                const { key, label } = section;
                const sectionPublic = isPublic(key);
                return (
                  <div
                    key={key}
                    className="rounded-xl border border-[#E2E8F0] bg-white px-5 py-4 shadow-[0_6px_16px_rgba(148,163,184,0.12)]"
                  >
                    <div className="flex items-center justify-between">
                      <h2 className="text-xs font-semibold text-charcoal uppercase tracking-widest">
                        {label}
                      </h2>
                      {!sectionPublic && (
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          Private
                        </span>
                      )}
                    </div>
                    <div className="mt-3 text-sm text-gray-700">
                      {!sectionPublic ? (
                        <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                          This section is private.
                        </div>
                      ) : key === "about" ? (
                        <p className="text-sm leading-6">
                          {sanitizeText(profile?.about) || "No summary added."}
                        </p>
                      ) : key === "skills" ? (
                        profile?.skills?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {profile.skills.map((skill) => (
                              <span
                                key={skill?._id || skill?.title || skill}
                                className="rounded-full bg-[#F1F5F9] px-3 py-1 text-xs text-gray-700"
                              >
                                {skill?.title || skill}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">
                            No skills added.
                          </span>
                        )
                      ) : key === "experience" ? (
                        profile?.workExperiences?.length ? (
                          <div className="space-y-3">
                            {profile.workExperiences.map((exp) => (
                              <div
                                key={exp?._id}
                                className="rounded-lg border border-gray-100 bg-[#F8FAFC] px-3 py-2"
                              >
                                <p className="font-medium text-gray-800">
                                  {exp?.jobTitle || "Role"}{" "}
                                  <span className="text-gray-500">
                                    {exp?.companyName || ""}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-500">
                                  {exp?.location || ""}
                                </p>
                                {exp?.jobDescription && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    {sanitizeText(exp.jobDescription)}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">
                            No experience added.
                          </span>
                        )
                      ) : key === "education" ? (
                        profile?.education?.length ? (
                          <div className="space-y-3">
                            {profile.education.map((edu) => (
                              <div
                                key={edu?._id}
                                className="rounded-lg border border-gray-100 bg-[#F8FAFC] px-3 py-2"
                              >
                                <p className="font-medium text-gray-800">
                                  {edu?.degree ||
                                    edu?.fieldOfStudy ||
                                    "Education"}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {edu?.school || ""}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">
                            No education added.
                          </span>
                        )
                      ) : key === "certifications" ? (
                        profile?.certifications?.length ? (
                          <div className="space-y-3">
                            {profile.certifications.map((cert) => (
                              <div
                                key={cert?._id}
                                className="rounded-lg border border-gray-100 bg-[#F8FAFC] px-3 py-2"
                              >
                                <p className="font-medium text-gray-800">
                                  {cert?.degree ||
                                    cert?.fieldOfStudy ||
                                    "Certification"}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">
                            No certifications added.
                          </span>
                        )
                      ) : key === "contact" ? (
                        profile?.email ||
                        profile?.phoneNo ||
                        profile?.address ? (
                          <div className="space-y-1 text-xs">
                            {profile?.email && <p>Email: {profile.email}</p>}
                            {profile?.phoneNo && (
                              <p>
                                Phone: {profile?.countryCode} {profile.phoneNo}
                              </p>
                            )}
                            {profile?.address && (
                              <p>Address: {profile.address}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">
                            Contact details are not available.
                          </span>
                        )
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfilePage;
